import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../Components/Layout';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import useCallOnce from '../hooks/useCallOnce';
import { getUserById, saveAgentPrompt } from '../redux/actions/userActions';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-tabpanel-${index}`}
      aria-labelledby={`prompt-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function PromptSettings({ selectedUserId }) {
  const dispatch = useDispatch();
  const { byId, promptSave } = useSelector((s) => s.users);

  const [userId, setUserId] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [promptText, setPromptText] = useState('');
  const [voicePromptText, setVoicePromptText] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [voicePromptFetched, setVoicePromptFetched] = useState(false);

  // Initialize from selectedUserId and fetch simple prompt once when tab opens
  useCallOnce('prompt/init', () => {
    const id = selectedUserId || '';
    if (id) {
      setUserId(id);
      dispatch(getUserById(id, 'simpleprompt'));
    }
  }, [selectedUserId, dispatch]);

  // Fetch voice prompt when voice tab is clicked
  useEffect(() => {
    if (activeTab === 1 && !voicePromptFetched && userId) {
      dispatch(getUserById(userId, 'voiceprompt'));
      setVoicePromptFetched(true);
    }
  }, [activeTab, voicePromptFetched, userId, dispatch]);

  // Update prompt text based on active tab
  useEffect(() => {
    const payload = byId?.data?.data || byId?.data || {};
    const existingPrompt = payload?.prompt || '';
    
    if (activeTab === 0) {
      setPromptText(existingPrompt);
    } else {
      setVoicePromptText(existingPrompt);
    }
  }, [byId, activeTab]);

  const loadingUser = byId.loading;
  const saving = promptSave.loading;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Fetch data for the selected tab
    if (newValue === 0 && userId) {
      dispatch(getUserById(userId, 'simpleprompt'));
    } else if (newValue === 1 && userId) {
      dispatch(getUserById(userId, 'voiceprompt'));
    }
  };

  const handleSave = () => {
    setConfirmDialogOpen(true);
  }; 

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);
    if (!userId) return;
    
    const currentPrompt = activeTab === 0 ? promptText : voicePromptText;
    const type = activeTab === 0 ? 'simpleprompt' : 'voiceprompt';
    
    await dispatch(saveAgentPrompt({ 
      userId, 
      prompt: currentPrompt, 
      type 
    }));
  };

  const handleCancelSave = () => {
    setConfirmDialogOpen(false);
  };

  const hasContent = activeTab === 0 
    ? promptText.trim().length > 0 
    : voicePromptText.trim().length > 0;

  return (
    <Box sx={{ width: '100%', minWidth: 0 , p: { xs: 2, sm: 4 }, mt: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Prompt Settings</Typography>

      {/* Tabs */}
      <Paper sx={{ mb: 2, border: '1px solid #e5e7eb' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="prompt tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Simple Prompt" />
          <Tab label="Voice Prompt" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Paper sx={{ p: 2, border: '1px solid #e5e7eb' }}>
          <Box sx={{ position: 'relative' }}>
            {loadingUser && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.6)',
                  zIndex: 2,
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            <TextField
              fullWidth
              multiline
              rows={20}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Enter prompt settings here..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'monospace',
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              onClick={handleSave} 
              disabled={!userId || saving || !hasContent} 
              sx={{ textTransform:'none' }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Paper sx={{ p: 2, border: '1px solid #e5e7eb' }}>
          <Box sx={{ position: 'relative' }}>
            {loadingUser && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.6)',
                  zIndex: 2,
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            <TextField
              fullWidth
              multiline
              rows={20}
              value={voicePromptText}
              onChange={(e) => setVoicePromptText(e.target.value)}
              placeholder="Enter voice prompt settings here..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'monospace',
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              onClick={handleSave} 
              disabled={!userId || saving || !hasContent} 
              sx={{ textTransform:'none' }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelSave}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Save
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to save these {activeTab === 0 ? 'simple' : 'voice'} prompt settings? This will update the agent's prompt configuration.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSave} 
            variant="contained"
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
